import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import { RedoMarketingRpcClientContext } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
import {
  CustomerSegmentOperation,
  ManageStaticSegmentStep,
} from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import { CustomerGroupType } from "@redotech/redo-model/customers/customer-group-definition";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import {
  LabeledInput,
  LabelOrientation,
  LabelPosition,
  LabelSize,
} from "@redotech/redo-web/labeled-input";
import { memo, useMemo, useState } from "react";
import { FrontendStepType, PartialWithRequiredType } from "../steps";
import { useDelayedAutoFocus } from "./common/use-delayed-auto-focus";

interface MinimalSegment {
  _id: string;
  name: string;
  type: CustomerGroupType;
}

const CreateSegmentModal = memo(function CreateSegmentModal({
  onSave,
  onCancel,
}: {
  onSave: (segment: MinimalSegment) => void;
  onCancel: () => void;
}) {
  const client = useRequiredContext(RedoMarketingRpcClientContext);
  const [name, setName] = useState("");

  const [saveLoad, triggerSave] = useTriggerLoad(async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      return;
    }
    const segment = await client.createStaticSegment({ name: trimmed });
    onSave({ _id: segment._id, name: segment.name, type: segment.type });
  });

  return (
    <RedoModal
      onModalCloseRequested={onCancel}
      primaryButton={{
        onClick: triggerSave,
        text: "Create segment",
        disabled: !name.trim() || saveLoad.pending,
      }}
      secondaryButton={{ onClick: onCancel, text: "Cancel" }}
      title="Create static segment"
    >
      <RedoTextInput
        placeholder="Segment name"
        setValue={setName}
        value={name}
      />
    </RedoModal>
  );
});

const StaticSegmentManager = memo(function StaticSegmentManager({
  step,
  setStep,
  showValidationErrors,
}: {
  step: PartialWithRequiredType<ManageStaticSegmentStep>;
  setStep: (step: PartialWithRequiredType<ManageStaticSegmentStep>) => void;
  showValidationErrors?: boolean;
}) {
  const client = useRequiredContext(RedoMarketingRpcClientContext);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [segmentReloadTrigger, setSegmentReloadTrigger] = useState(0);
  const [segmentDropdownTouched, setSegmentDropdownTouched] = useState(
    showValidationErrors || false,
  );

  const segmentsLoad = useLoad(
    async (signal) =>
      client.getCustomerGroupsMinimal(
        { type: CustomerGroupType.STATIC },
        { signal },
      ),
    [segmentReloadTrigger],
  );

  const operationOptions = useMemo(
    () => [
      {
        id: "add",
        text: "Add to segment",
        type: "text" as const,
        value: CustomerSegmentOperation.ADD,
      },
      {
        id: "remove",
        text: "Remove from segment",
        type: "text" as const,
        value: CustomerSegmentOperation.REMOVE,
      },
    ],
    [],
  );

  const segmentOptions: RedoListItem<MinimalSegment>[] = useMemo(
    () =>
      segmentsLoad.value?.map((s) => ({
        id: s._id,
        type: "text" as const,
        text: s.name,
        value: s,
      })) ?? [],
    [segmentsLoad.value],
  );

  const selectedOperation = useMemo(
    () => operationOptions.find((op) => op.value === step.operation),
    [operationOptions, step.operation],
  );

  const selectedSegment = useMemo(
    () => segmentOptions.find((opt) => opt.value._id === step.segmentId),
    [segmentOptions, step.segmentId],
  );

  const handleOperationSelect = useHandler((option: any) => {
    setStep({ ...step, operation: option.value });
  });

  const handleSegmentSelect = useHandler(
    ({ item }: { item: RedoListItem<MinimalSegment> }) => {
      setStep({ ...step, segmentId: item.value._id });
    },
  );

  const handleCreateSegment = useHandler((segment: MinimalSegment) => {
    setSegmentReloadTrigger((prev) => prev + 1);
    setStep({ ...step, segmentId: segment._id });
    setShowCreateModal(false);
  });

  const hasNoSegment = !step.segmentId;

  const errorText = useMemo(() => {
    if (hasNoSegment && segmentDropdownTouched) {
      return "A segment is required";
    }
    return undefined;
  }, [hasNoSegment, segmentDropdownTouched]);

  const delayedAutoFocus = useDelayedAutoFocus(hasNoSegment);

  return (
    <Flex dir="column" gap="md">
      <LabeledInput
        label="Operation"
        orientation={LabelOrientation.VERTICAL}
        position={LabelPosition.ABOVE}
        size={LabelSize.EXTRA_SMALL}
      >
        <RedoSingleSelectDropdownInput
          options={operationOptions}
          optionSelected={({ item }: { item: any }) =>
            handleOperationSelect(item)
          }
          placeholder="Select operation..."
          selectedItem={selectedOperation}
        />
      </LabeledInput>

      <LabeledInput
        label="Static segment"
        orientation={LabelOrientation.VERTICAL}
        position={LabelPosition.ABOVE}
        size={LabelSize.EXTRA_SMALL}
      >
        <Flex dir="column" gap="sm" w="full">
          <RedoButton
            hierarchy="secondary"
            IconLeading={PlusIcon}
            onClick={() => setShowCreateModal(true)}
            size="sm"
            text="New segment"
          />
          <RedoSingleSelectDropdownInput
            autoFocus={delayedAutoFocus}
            description={errorText}
            itemsLoading={segmentsLoad.pending}
            key={delayedAutoFocus.toString()}
            onBlur={() => setSegmentDropdownTouched(true)}
            options={segmentOptions}
            optionSelected={handleSegmentSelect}
            placeholder="Select a segment..."
            selectedItem={selectedSegment}
            state={errorText ? "error" : "default"}
          />
        </Flex>
      </LabeledInput>

      {showCreateModal && (
        <CreateSegmentModal
          onCancel={() => setShowCreateModal(false)}
          onSave={handleCreateSegment}
        />
      )}
    </Flex>
  );
});

export const validateManageStaticSegmentStep = (
  step: PartialWithRequiredType<ManageStaticSegmentStep>,
) => {
  return !!(step.operation && step.segmentId);
};

export const manageStaticSegmentStepType: FrontendStepType<
  PartialWithRequiredType<ManageStaticSegmentStep>
> = {
  valid: validateManageStaticSegmentStep,
  type: "standard",
  inspectorFactory: (props) => ({
    header: { title: "Manage static segments" },
    sections: [
      {
        fields: [
          { widget: "toggle", label: "Deactivate step", path: "disabled" },
          {
            path: "staticSegment",
            label: "",
            widget: "custom",
            Element: ({ value: step, valueChange: setStep }) => (
              <StaticSegmentManager
                setStep={setStep}
                showValidationErrors={props.showValidationErrors}
                step={step}
              />
            ),
          },
        ],
      },
    ],
  }),
};
